import type { SlideShowSlide } from "@ui/molecules/SlideShow/SlideShow.tsx";

/** Hydrated csv `artifact_created` payload (and the `AssetPreviewCSV` contract). */
export type CSVAssetPreview = {
	headers: string[];
	rows_preview: string[][];
	total_rows?: number;
	title?: string;
};

/** GVT influencer profile (aligned with public profile / InfluencerDetailedInfo). */
export type VideoEvidenceInfluencer = {
	id?: string;
	handle?: string;
	display_name?: string;
	profile_picture_url?: string | null;
	profile_picture_social_account_id?: string | null;
	follower_count?: number;
	platforms?: Array<{
		platform: string;
		handle: string;
		profile_url?: string;
		follower_count?: number;
	}>;
} & Record<string, unknown>;

export type VideoEvidence = {
	video_url: string;
	/**
	 * Presigned URL for the clip's first frame. It can 404 until the server's
	 * queued extraction stores it, so render it through usePendingImage.
	 */
	thumbnail_url?: string | null;
	aspect_ratio?: number | null;
	start_time: number;
	end_time: number;
	/** Full source video duration in seconds. */
	video_duration: number;
	/** Trimmed clip duration in seconds (`end_time - start_time`). */
	clip_duration: number;
	/**
	 * Clip start offset in the full source video (seconds). Defaults to
	 * `start_time`, so producers whose `start_time` is clip-relative (board
	 * clips) must set it explicitly.
	 */
	source_start_time?: number;
	description: string;
	/** Clip title: LangChain source summary when present, else the post title. */
	title?: string | null;
	transcript?: string | null;
	video_id: string;
	/**
	 * Set only when `video_url` is this video's whole source (so `start_time`
	 * and `end_time` are source-absolute): its public id, which lets the
	 * player stream the clip from the full video's HLS package instead
	 * (videoEvidenceHlsSource). Leave it unset for a pre-cut clip asset (board
	 * clips' preview_mp4), whose clip-relative times HLS can't line up with --
	 * and distinct from `video_id` above, which board clips set to the clip's
	 * own id.
	 */
	source_video_id?: string | null;
	/**
	 * False when the server knows `source_video_id` has no playable HLS
	 * package, so the player goes straight to `video_url`. Unset (older
	 * payloads) means try HLS and fall back.
	 */
	hls_ready?: boolean;
	influencer: VideoEvidenceInfluencer;
	social_account?: {
		follower_count: number;
		handle: string;
		platform: string;
		profile_picture_url: string;
		profile_url: string;
	};
	post_url: string;
	/** Post view count when the source video/post is resolvable. */
	view_count?: number | null;
};

/**
 * A single hydrated entry inside a `video_sources` artifact. Mirrors the
 * transformed `video_evidence` clip shape, except the clip summary is carried
 * as `content_summary` and the internal `id` has been stripped.
 */
export type VideoSource = {
	video_url?: string;
	thumbnail_url?: string | null;
	aspect_ratio?: number | null;
	start_time?: number;
	end_time?: number;
	video_duration?: number;
	clip_duration?: number;
	source_start_time?: number;
	content_summary?: string | null;
	title?: string | null;
	/** Parent video / post title. `title` is the LangChain clip summary when present. */
	video_title?: string | null;
	transcript?: string | null;
	video_id?: string;
	hls_ready?: boolean;
	influencer?: VideoEvidenceInfluencer | null;
	social_account?: VideoEvidence["social_account"] | null;
	post_url?: string;
	view_count?: number | null;
};

/** Downloadable file attached to a hydrated artifact (`artifact.outputs`). */
export type ArtifactOutput = {
	format: string;
	format_label: string;
	url: string;
	/**
	 * PDFs only: the same object signed for inline display. `url` is signed as an
	 * attachment, which browsers download rather than render in a viewer.
	 */
	preview_url?: string;
};

/**
 * One presigned page image of a hydrated `document` artifact. `width_px` and
 * `height_px` are the thumbnail's intrinsic size, used to reserve layout space.
 */
export type DocumentPageThumbnail = {
	url: string;
	page?: number;
	width_px?: number;
	height_px?: number;
};

/**
 * One clip of a hydrated `board` artifact. `video_id` is the source video's
 * public id (null when the video is gone), and `thumbnail_url` is its frame at
 * `start_time_sec`, because the clip itself may still be rendering.
 */
export type BoardArtifactClip = {
	clip_id: string;
	video_id: string | null;
	start_time_sec: number;
	end_time_sec: number;
	title: string;
	thumbnail_url: string | null;
	aspect_ratio: number | null;
};

/**
 * Payload of an `artifact_created` event's `content`. Stored keyed on `id` and
 * later rendered when referenced by a matching `artifact_mention`. The renderer
 * is selected by `type`; currently `video_sources`, `deck`, `csv`, `document`
 * and `board` are supported.
 */
export type ArtifactContent = {
	id: string;
	type: string;
	/** Asset-registry public_id when the artifact was autosaved (stream post-processing). */
	document_id?: string;
	is_favourite?: boolean;
	artifact?: {
		sources?: VideoSource[];
		slides?: SlideShowSlide[];
		outputs?: ArtifactOutput[];
		thumbnails?: DocumentPageThumbnail[];
		/** A plain string on `document`, but `{ headline, summary }` on `deck`. */
		summary?: string | { headline?: string; summary?: string };
		/** `board` only: whether the agent created the board or reused one. */
		did_create?: boolean;
		clips_added?: BoardArtifactClip[];
		clips_already_on_board?: BoardArtifactClip[];
		/**
		 * `board` only, looked up when the stream is post-processed; all absent
		 * when the lookup couldn't be made, `deleted` alone when it found nothing.
		 */
		deleted?: boolean;
		privacy?: string;
		updated_at?: string;
		clip_count?: number;
	} & Partial<CSVAssetPreview> &
		Record<string, unknown>;
	timestamp_nanoseconds?: number;
};

export function artifactDownloadOutputs(
	artifact: ArtifactContent["artifact"] | undefined,
): ArtifactOutput[] {
	const outputs = artifact?.outputs;
	if (!Array.isArray(outputs)) {
		return [];
	}
	return outputs.filter(
		(output): output is ArtifactOutput =>
			output != null &&
			typeof output === "object" &&
			typeof output.format === "string" &&
			output.format.length > 0 &&
			typeof output.format_label === "string" &&
			output.format_label.length > 0 &&
			typeof output.url === "string" &&
			output.url.length > 0,
	);
}

/** What `DocumentView` needs from a hydrated `document` artifact. */
export type DocumentPreview = {
	title: string;
	summary?: string;
	pages: DocumentPageThumbnail[];
	/**
	 * Inline URL of the PDF output, for the embedded viewer. Absent when the
	 * document was generated as Word, since only the requested format is kept.
	 */
	pdfUrl?: string;
};

/**
 * Normalises a `document` artifact for rendering. The event carries no title
 * yet, so a generic one stands in until it does. Only a string `summary` is
 * kept, since decks carry an object under the same key. Pages without a
 * presigned `url` are dropped - post-processing leaves the S3 fields in place
 * when a bucket/key pair cannot be signed.
 */
export function documentPreview(
	artifact: ArtifactContent["artifact"] | undefined,
): DocumentPreview {
	const title = artifact?.title;
	const summary = artifact?.summary;
	const thumbnails = artifact?.thumbnails;
	const pdf = artifactDownloadOutputs(artifact).find(
		(output) => output.format.toLowerCase() === "pdf",
	);

	return {
		title:
			typeof title === "string" && title.trim().length > 0 ? title : "Document",
		summary:
			typeof summary === "string" && summary.trim().length > 0
				? summary
				: undefined,
		pages: Array.isArray(thumbnails)
			? thumbnails.filter(
					(page): page is DocumentPageThumbnail =>
						page != null &&
						typeof page === "object" &&
						typeof page.url === "string" &&
						page.url.length > 0,
				)
			: [],
		pdfUrl:
			typeof pdf?.preview_url === "string" && pdf.preview_url.length > 0
				? pdf.preview_url
				: undefined,
	};
}
